import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { useTenant } from "../../contexts/TenantContext";
import { checkIn, checkOut, checkinHandoff, requestMagicLink } from "../../api/backend";
import CheckinShell, { CheckinAlert, SessionCard, formatTime, resolveBrand } from "./CheckinShell";
import RosterPick from "./RosterPick";
import { checkinHandoffPath } from "./checkinPaths";
import { getDeviceSecret } from "./checkinDevice";
import { getPosition } from "./getPosition";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

function Note({ children }) {
  return <div className="ck-note"><InfoOutlinedIcon sx={{ fontSize: 15 }} />{children}</div>;
}

function SignedInView({ state, brand, onCheckOut, leaving, leaveError }) {
  const { status, result, hadPosition, message } = state;
  const session = result?.session;
  const checkedOutAt = result?.checkedOutAt;
  const done = status === "done" || status === "already" || status === "out";
  const heading = {
    done: "You're checked in",
    already: "You're already checked in",
    out: "You're checked out",
  }[status];
  const eyebrow = { done: "Checked in", already: "Already checked in", out: "Checked out" }[status];
  const stamp = status === "out" ? checkedOutAt : result?.checkedInAt;

  return (
    <CheckinShell brand={brand}>
      {status === "working" && (
        <div className="ck-spin">
          <CircularProgress aria-label="Checking you in" />
          <span>Checking you in…</span>
        </div>
      )}

      {done && (
        <>
          <div className="ck-center" role="status">
            <div className={`ck-done${status === "out" ? " ck-done--out" : ""}`}><CheckRoundedIcon sx={{ fontSize: 38 }} /></div>
            <div className="ck-eyebrow">{eyebrow}</div>
            <h1>{heading}</h1>
            {stamp && <p>{status === "out" ? "Left" : "Recorded"} at <span className="ck-time">{formatTime(stamp)}</span></p>}
            {status === "already" && checkedOutAt && <p>You checked out at {formatTime(checkedOutAt)}.</p>}
          </div>
          <SessionCard session={session} />
          {status === "done" && result?.locationValid === false && (
            <CheckinAlert warn>
              Your location didn&apos;t match the room, so your instructor may review this check-in.
            </CheckinAlert>
          )}
          {status === "done" && !hadPosition && (
            <Note>
              We couldn&apos;t confirm your location. You&apos;re still checked in; allow location access next time to
              have it recorded.
            </Note>
          )}
          {status === "already" && !checkedOutAt && (
            <div className="ck-stack">
              <Note>Leaving? Scan again when you go.</Note>
              <button type="button" className="ck-btn ck-btn--o" onClick={onCheckOut} disabled={leaving}>
                {leaving ? "Checking out…" : "Check out"}
              </button>
            </div>
          )}
          {leaveError && <CheckinAlert>{leaveError}</CheckinAlert>}
        </>
      )}

      {status === "error" && <CheckinAlert>{message}</CheckinAlert>}
      {status === "missing" && (
        <div>
          <div className="ck-eyebrow">Check in</div>
          <h1 className="ck-h1--sm">Scan the QR code shown by your instructor to check in.</h1>
        </div>
      )}
    </CheckinShell>
  );
}

function MagicLinkSent({ email }) {
  return (
    <>
      <div>
        <div className="ck-eyebrow">Check your inbox</div>
        <h1>Check your email</h1>
      </div>
      <div className="ck-sent">
        <CheckRoundedIcon sx={{ fontSize: 18 }} />
        <div>
          We sent a sign-in link to <b>{email}</b>. Open it in this same browser on this phone and you&apos;ll be checked in.
          It works once and expires in 10 minutes.
        </div>
      </div>
    </>
  );
}

function SignedOutView({ state, brand, onSignIn, device }) {
  const [email, setEmail] = React.useState("");
  const [tried, setTried] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);
  const [picking, setPicking] = React.useState(false);
  const emailBad = tried && !EMAIL_RE.test(email.trim());
  const session = state.session || (state.sessionName ? { name: state.sessionName } : null);

  const sendLink = async (e) => {
    e.preventDefault();
    setTried(true);
    if (!EMAIL_RE.test(email.trim())) return;
    setSending(true);
    setSendError(null);
    try {
      await requestMagicLink(email.trim(), checkinHandoffPath(state.ticket));
      setSent(true);
    } catch {
      setSendError("Couldn't send the link. Please try again.");
    } finally {
      setSending(false);
    }
  };

  let body;
  if (picking) {
    body = <RosterPick token={state.scanToken} handoff={state.ticket} device={device} session={session} />;
  } else if (state.status === "working") {
    body = <div className="ck-spin"><CircularProgress size={24} aria-label="Getting ready" /></div>;
  } else if (state.status === "missing") {
    body = (
      <div>
        <div className="ck-eyebrow">Check in</div>
        <h1 className="ck-h1--sm">Scan the QR code shown by your instructor to check in.</h1>
      </div>
    );
  } else if (state.status === "error") {
    body = (
      <>
        <div>
          <div className="ck-eyebrow">Check in</div>
          <h1 className="ck-h1--sm">Couldn&apos;t start check-in</h1>
        </div>
        <CheckinAlert>{state.message}</CheckinAlert>
        <Note>Scan the code on your instructor&apos;s screen again.</Note>
      </>
    );
  } else if (sent) {
    body = <MagicLinkSent email={email.trim()} />;
  } else {
    body = (
      <>
        <div>
          <div className="ck-eyebrow">Check in</div>
          <h1>Sign in to check in to this session</h1>
        </div>
        <SessionCard session={session} />
        <button type="button" className="ck-btn ck-btn--p" onClick={onSignIn}>
          <VpnKeyOutlinedIcon sx={{ fontSize: 18 }} />Sign in &amp; check in
        </button>
        <div className="ck-or">or use a one-time link</div>
        {sendError && <CheckinAlert>{sendError}</CheckinAlert>}
        <form className="ck-stack" onSubmit={sendLink} noValidate>
          <label className="ck-lbl" htmlFor="checkin-email">Email</label>
          <span className={`ck-inp${emailBad ? " is-bad" : ""}`}>
            <MailOutlineIcon sx={{ fontSize: 18 }} />
            <input
              id="checkin-email" type="email" inputMode="email" autoComplete="email" placeholder="you@department.org"
              value={email} onChange={(e) => setEmail(e.target.value)}
              aria-invalid={emailBad ? "true" : undefined}
            />
          </span>
          {emailBad && <small className="ck-err">Enter a valid email address.</small>}
          <button type="submit" className="ck-btn ck-btn--o" disabled={sending}>
            {sending ? "Sending…" : "Email me a link"}
          </button>
        </form>
        {state.rosterPickEnabled && state.scanToken && (
          <div className="ck-alt">
            Can&apos;t sign in?<br />
            <button type="button" onClick={() => setPicking(true)}>Find my name on the roster</button>
          </div>
        )}
      </>
    );
  }

  return <CheckinShell brand={brand} onBack={picking ? () => setPicking(false) : undefined}>{body}</CheckinShell>;
}

/**
 * Where the QR code lands. Signed in: checks the student in automatically, with their location when the browser will
 * share it; a second scan offers to check them out. Signed out: trades the fresh scan for a handoff ticket (bound to
 * this browser, so it can't expire while they sign in and can't be forwarded), then offers a password sign-in, an
 * emailed link, or, when the instructor allows it, picking their own name. All of them return here. Every state is
 * branded with the training center that owns the session.
 */
export default function CheckinPage() {
  const { user, domainTenant, tenantConfig } = useTenant();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("t");
  const handoff = params.get("h");
  const [state, setState] = React.useState({ status: "working" });
  const [leaving, setLeaving] = React.useState(false);
  const [leaveError, setLeaveError] = React.useState(null);
  const started = React.useRef(false);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    const update = (next) => { if (mounted.current) setState(next); };

    if (!token && !handoff) {
      update({ status: "missing" });
      return;
    }

    if (user) {
      (async () => {
        const position = await getPosition();
        const scan = token ? { token } : { handoff, device: getDeviceSecret() };
        try {
          const result = await checkIn({ ...scan, ...(position || {}) });
          update({
            status: result.alreadyCheckedIn ? "already" : "done", result, hadPosition: !!position, position, scan,
          });
        } catch (err) {
          update({ status: "error", message: messageOf(err, "Couldn't check you in. Try scanning again.") });
        }
      })();
      return;
    }

    if (handoff) {
      update({ status: "signin", ticket: handoff });
      return;
    }
    checkinHandoff(token, getDeviceSecret())
      .then((data) => {
        update({
          status: "signin", ticket: data.handoff, sessionName: data.sessionName, session: data.session,
          tenant: data.tenant, rosterPickEnabled: !!data.rosterPickEnabled, scanToken: token,
        });
        // The scan token has served its purpose here; keep it out of the address bar and history.
        navigate(checkinHandoffPath(data.handoff), { replace: true });
      })
      .catch((err) => update({ status: "error", message: messageOf(err, "This QR code isn't valid. Scan it again.") }));
  }, [user, token, handoff, navigate]);

  const handleCheckOut = async () => {
    setLeaving(true);
    setLeaveError(null);
    try {
      const result = await checkOut({ ...state.scan, ...(state.position || {}) });
      if (mounted.current) setState((prev) => ({ ...prev, status: "out", result }));
    } catch (err) {
      if (mounted.current) setLeaveError(messageOf(err, "Couldn't check you out. Scan the code again."));
    } finally {
      if (mounted.current) setLeaving(false);
    }
  };

  // The session's own center wins; a signed-in user's center is the same one, and the domain's is the last resort.
  const brand = resolveBrand(state.tenant, user ? tenantConfig : null, domainTenant);

  if (user) {
    return <SignedInView state={state} brand={brand} onCheckOut={handleCheckOut} leaving={leaving} leaveError={leaveError} />;
  }
  return (
    <SignedOutView
      state={state}
      brand={brand}
      device={getDeviceSecret()}
      onSignIn={() => navigate("/login", { state: { redirectTo: checkinHandoffPath(state.ticket) } })}
    />
  );
}
